import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, ExternalLink, Copy, FileDown, ScanLine } from "lucide-react";
import { api, partnerPublicUrl } from "../../lib/api";
import { ImageUploadField } from "./ImageUploadField";

const opts = { withCredentials: true };
const EMPTY = { name: "", code: "", city: "", logo_url: "", offer_label: "", offer_url: "", extract_ids: [] };
const inputCls = "w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm";
const errMsg = (e, f) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);

const In = ({ k, label, form, setForm, placeholder, hint }) => (
  <div>
    <label className="text-xs font-body text-[#4A4A4A] mb-1 block">{label}</label>
    <input value={form[k] || ""} placeholder={placeholder} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`partner-${k}-input`} className={inputCls} />
    {hint && <p className="text-[11px] font-body text-[#757575] mt-1">{hint}</p>}
  </div>
);

export function PartnersPanel() {
  const [items, setItems] = useState([]);
  const [extracts, setExtracts] = useState([]);
  const [form, setForm] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const load = () => api.get("/admin/partners", opts).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/admin/extracts", opts).then((r) => setExtracts(r.data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Le nom du partenaire est obligatoire");
    const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] === "" ? null : form[k]]));
    payload.extract_ids = form.extract_ids || [];
    try {
      if (form.id) await api.put(`/admin/partners/${form.id}`, payload, opts);
      else await api.post("/admin/partners", payload, opts);
      toast.success("Partenaire enregistré");
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e, "Erreur d'enregistrement")); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ? Son QR code ne fonctionnera plus.`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`, opts);
      toast.success("Partenaire supprimé");
      load();
    } catch (e) { toast.error(errMsg(e, "La suppression a échoué")); }
  };
  const report = async (p) => {
    try {
      const r = await api.get(`/admin/partners/${p.id}/report`, { ...opts, params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `rapport-${p.code}-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Impossible de générer le rapport"); }
  };
  const copy = (p) => navigator.clipboard?.writeText(partnerPublicUrl(p.code)).then(() => toast.success("Lien copié"));
  const titleOf = (id) => extracts.find((e) => e.id === id)?.title;

  return (
    <div data-testid="partners-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Partenaires ({items.length})</h2>
        <div className="flex items-center gap-3">
          <label className="text-xs font-body text-[#4A4A4A] flex items-center gap-2">Rapport du mois
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="partner-report-month" className="border border-[#D1CFC7] bg-[#FAF9F6] px-2 py-1.5 text-xs" />
          </label>
          <button onClick={() => setForm({ ...EMPTY })} data-testid="new-partner-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> Nouveau partenaire</button>
        </div>
      </div>
      <p className="text-sm font-body text-[#757575] mb-8 max-w-3xl">Un partenaire = un seul QR code. En le scannant, le lecteur arrive sur une page avec toutes les histoires que vous lui attribuez, classées par genre.</p>
      {form && <PartnerForm form={form} setForm={setForm} extracts={extracts} onSave={save} onCancel={() => setForm(null)} />}
      <ul className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-8">
        {items.map((p) => (
          <li key={p.id} data-testid={`partner-card-${p.code}`} className="flex gap-4 border border-[#D1CFC7] p-4">
            <div className="bg-white p-1.5 border border-[#D1CFC7] self-start flex-shrink-0"><QRCodeSVG value={partnerPublicUrl(p.code)} size={72} /></div>
            <div className="flex-1 min-w-0">
              {p.logo_url && <img src={p.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid={`partner-logo-thumb-${p.code}`} className="h-8 w-auto max-w-[120px] object-contain mb-2" />}
              <p className="font-serif text-lg text-[#1A1A1A] truncate">{p.name}{p.city ? <span className="text-[#757575] text-sm"> — {p.city}</span> : null}</p>
              <p className="text-xs font-mono text-[#757575] truncate">/p/{p.code}</p>
              <p className="text-xs font-body text-[#4A4A4A] mt-1 flex items-center gap-1" data-testid={`partner-scans-${p.code}`}><ScanLine size={12} className="text-[#9E4735]" /> {p.scans} scan{p.scans > 1 ? "s" : ""}</p>
              <p className="text-xs font-body text-[#4A4A4A] mt-1" data-testid={`partner-stories-${p.code}`}>{p.extract_ids.length ? p.extract_ids.map(titleOf).filter(Boolean).join(" · ") : "Aucune histoire attribuée"}</p>
              <div className="flex flex-wrap gap-3 mt-3">
                <a href={`/p/${p.code}/affiche`} target="_blank" rel="noreferrer" data-testid={`partner-poster-${p.code}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Printer size={12} /> Imprimer son affiche</a>
                <a href={`/p/${p.code}`} target="_blank" rel="noreferrer" data-testid={`partner-page-${p.code}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><ExternalLink size={12} /> Voir sa page</a>
                <button onClick={() => copy(p)} data-testid={`partner-copy-${p.code}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Copy size={12} /> Copier le lien</button>
                <button onClick={() => report(p)} data-testid={`partner-report-${p.code}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><FileDown size={12} /> Rapport PDF</button>
                <button onClick={() => setForm({ ...EMPTY, ...p })} data-testid={`partner-edit-${p.code}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
                <button onClick={() => remove(p)} data-testid={`partner-delete-${p.code}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PartnerForm = ({ form, setForm, extracts, onSave, onCancel }) => {
  const toggle = (id) => setForm({ ...form, extract_ids: form.extract_ids.includes(id) ? form.extract_ids.filter((x) => x !== id) : [...form.extract_ids, id] });
  return (
    <div data-testid="partner-form" className="border border-[#2C402E] bg-[#F4F1EA] p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <h3 className="md:col-span-2 font-serif text-2xl text-[#1A1A1A]">{form.id ? "Modifier le partenaire" : "Nouveau partenaire"}</h3>
      <In k="name" label="Nom du partenaire *" form={form} setForm={setForm} placeholder="La Maison du Littéraire" />
      <In k="city" label="Ville" form={form} setForm={setForm} />
      <In k="code" label="Code (dans le lien /p/…)" form={form} setForm={setForm} placeholder="auto depuis le nom" hint="Attention : le modifier invalide les QR codes déjà imprimés." />
      <ImageUploadField label="Logo du partenaire" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="partner-logo-input" />
      <In k="offer_label" label="Offre du partenaire (texte du bouton, optionnel)" form={form} setForm={setForm} placeholder="Découvrez notre offre épargne" />
      <In k="offer_url" label="Lien de l'offre" form={form} setForm={setForm} placeholder="https://…" />
      <div className="md:col-span-2">
        <p className="text-xs font-body text-[#4A4A4A] mb-2">Histoires attribuées ({form.extract_ids.length}) :</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {extracts.map((e) => (
            <label key={e.id} className={`flex items-center gap-2 border px-3 py-2 text-sm font-body cursor-pointer transition-colors ${form.extract_ids.includes(e.id) ? "border-[#2C402E] bg-[#FAF9F6]" : "border-[#D1CFC7]"}`}>
              <input type="checkbox" checked={form.extract_ids.includes(e.id)} onChange={() => toggle(e.id)} data-testid={`partner-extract-${e.slug}`} />
              <span className="truncate">{e.title}{!e.published && <em className="text-[#757575]"> (brouillon)</em>}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="md:col-span-2 flex gap-2">
        <button onClick={onSave} data-testid="save-partner" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
        <button onClick={onCancel} data-testid="cancel-partner" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
      </div>
    </div>
  );
};
